import { useState, useRef, useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import Popper from '@mui/material/Popper';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';

// ----------------------------------------------------------------------

interface MentionUser {
  id: string;
  first_name: string;
  last_name: string;
}

type Props = TextFieldProps & {
  name: string;
  users?: MentionUser[];
  onMentionChange?: (mentionedUsers: MentionUser[]) => void;
};

export default function RHFMentionTextField({
  name,
  helperText,
  users = [],
  onMentionChange,
  ...other
}: Props) {

  const { control } = useFormContext();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [suggestions, setSuggestions] = useState<MentionUser[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mentionStart, setMentionStart] = useState<number | null>(null);
  const [taggedUsers, setTaggedUsers] = useState<MentionUser[]>([]);

  const textFieldRef = useRef<HTMLTextAreaElement | null>(null);

  // extract mentioned users from text by searching for each user's full name after @
  const extractAndUpdateMentionedUsers = useCallback((text: string) => {
    const mentioned: MentionUser[] = [];
    const seenIds = new Set<string>();

    // For each user, check if their full name appears after @ in the text
    users.forEach(user => {
      const fullName = `${user.first_name} ${user.last_name}`;
      const searchPattern = `@${fullName}`;

      // Check if the user's name appears in the text (case-insensitive)
      if (text.toLowerCase().includes(searchPattern.toLowerCase())) {
        // Only add unique users
        if (!seenIds.has(user.id)) {
          seenIds.add(user.id);
          mentioned.push(user);
        }
      }
    });

    setTaggedUsers(mentioned);

    if (onMentionChange) {
      onMentionChange(mentioned);
    }
  }, [users, onMentionChange]);

  const handleInputChange = useCallback((
    value: string,
    onChange: (value: string) => void
  ) => {
    onChange(value);

    // Extract and update mentioned users from the text
    extractAndUpdateMentionedUsers(value);

    if (!textFieldRef.current) return;

    const cursorPosition = textFieldRef.current.selectionStart || 0;
    const textBeforeCursor = value.substring(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    // check if we're in a mention context
    if (lastAtIndex !== -1) {
      const charBeforeAt = lastAtIndex > 0 ? value[lastAtIndex - 1] : ' ';
      const isValidMentionStart = charBeforeAt === ' ' || charBeforeAt === '\n' || lastAtIndex === 0;

      if (isValidMentionStart) {
        const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);

        // only show suggestions if there's no space or newline after @
        if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
          setMentionStart(lastAtIndex);

          // filter users
          const filtered = users.filter(user => {
            const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
            return fullName.includes(textAfterAt.toLowerCase());
          });

          setSuggestions(filtered);
          setSelectedIndex(0);

          if (filtered.length > 0) {
            setAnchorEl(textFieldRef.current);
          } else {
            setAnchorEl(null);
          }
          return;
        }
      }
    }

    setAnchorEl(null);
    setMentionStart(null);
  }, [users, extractAndUpdateMentionedUsers]);

  const insertMention = useCallback((
    user: MentionUser,
    currentValue: string,
    onChange: (value: string) => void
  ) => {
    if (mentionStart === null || !textFieldRef.current) return;

    const cursorPosition = textFieldRef.current.selectionStart || 0;
    const beforeMention = currentValue.substring(0, mentionStart);
    const afterMention = currentValue.substring(cursorPosition);

    // format: @firstname lastname
    const mentionText = `@${user.first_name} ${user.last_name}`;
    const newValue = `${beforeMention + mentionText  } ${  afterMention}`;

    onChange(newValue);

    extractAndUpdateMentionedUsers(newValue);

    setAnchorEl(null);
    setMentionStart(null);
    setSuggestions([]);

    setTimeout(() => {
      if (textFieldRef.current) {
        const newPosition = beforeMention.length + mentionText.length + 1;
        textFieldRef.current.setSelectionRange(newPosition, newPosition);
        textFieldRef.current.focus();
      }
    }, 0);
  }, [mentionStart, extractAndUpdateMentionedUsers]);

  const handleKeyDown = useCallback((
    e: React.KeyboardEvent,
    currentValue: string,
    onChange: (value: string) => void
  ) => {
    if (anchorEl && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % suggestions.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
      } else if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        insertMention(suggestions[selectedIndex], currentValue, onChange);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setAnchorEl(null);
        setMentionStart(null);
      }
    }
  }, [anchorEl, suggestions, selectedIndex, insertMention]);

  return (
    <>
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => {
          const handleClick = (user: MentionUser) => {
            insertMention(user, field.value || '', field.onChange);
          };

          return (
            <>
              <TextField
                {...field}
                fullWidth
                inputRef={textFieldRef}
                onChange={(event) => {
                  handleInputChange(event.target.value, field.onChange);
                }}
                onKeyDown={(e) => handleKeyDown(e, field.value || '', field.onChange)}
                error={!!error}
                helperText={error ? error?.message : helperText}
                {...other}
              />

              <Popper
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                placement="bottom-start"
                style={{ zIndex: 1300 }}
              >
                <Paper elevation={8} sx={{ maxHeight: 200, overflow: 'auto', minWidth: 250 }}>
                  <List dense>
                    {suggestions.length > 0 ? (
                      suggestions.map((user, index) => (
                        <ListItem key={user.id} disablePadding>
                          <ListItemButton
                            selected={index === selectedIndex}
                            onClick={() => handleClick(user)}
                          >
                            <ListItemText
                              primary={`${user.first_name} ${user.last_name}`}
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))
                    ) : (
                      <ListItem>
                        <ListItemText
                          primary="No users found"
                          primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                        />
                      </ListItem>
                    )}
                  </List>
                </Paper>
              </Popper>
            </>
          );
        }}
      />
    </>
  );
}